import * as React from 'react';
import { Link } from 'react-router-dom';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import { Box, ModalProps, Paper, Stack, Typography } from '@mui/material';
import MuiIconButton from '@mui/material/IconButton';
import { styled } from '@mui/system';

import { useModal } from 'mui-modal-provider';

import { environment } from '../../../../config';
import { VenueReservationContentItemV2 } from '../../../../graphql/API';
import { useAppSelector } from '../../../../redux/hooks';
import { centsToDollars } from '../../../../utils/formatter';
import { VENUE_MANUAL_TAGS } from '../../../../utils/venue-tags';
import Button from '../../../Button/Button';
import { FeedIcon, ReelIcon, StoryIcon } from '../../../Icons';
import BuyContentDialog from '../../BuyContent/BuyContentDialog';
import { ConfirmAction } from '../../Confirm/context/types';
import UsageRightsDialog from '../../UsageRights/UsageRightsDialog';
import { ActionBuyDownload } from './ActionBuyDownload';
import { ActionBuyDownloadBeforeUpdate } from './ActionBuyDownloadBeforeUpdate';
import { BuyContent } from './BuyContent';
import MediaContent from './MediaContent';
import { NoPriceBuyContent } from './NoPriceBuyContent';
import UserHeader from './UserHeader';
import useAction from './useAction';

const baseUrl = (() => {
  const envUrl = environment === 'production' ? '' : `-${environment}`;
  return `https://assets${envUrl}.beautypass.app`;
})();

const IconButton = styled(MuiIconButton)({
  backgroundColor: 'white',
  ':hover': {
    backgroundColor: 'rgb(255,255,255,0.84)',
  },
});

export type ContentProps = {
  itemId: number;
  items: VenueReservationContentItemV2[];
  onClose?: ModalProps['onClose'];
  confirm: ConfirmAction;
};

export const BUY_CONTENT_COST = 5.0;

const Content: React.FC<ContentProps> = ({ onClose, ...restProps }) => {
  const { balance, status } = useAppSelector((state) => state.balance.value) || {};
  const { tags: venueTags } = useAppSelector((state) => state.venue.value) || {};

  const availableBalance = Number(centsToDollars(balance || 0));

  const { showModal } = useModal();
  const {
    item,
    handleDownloadContent,
    handleInviteUser,
    handleBuyContent,
    handleSaveUser,
    handleNavigation,
    getLoading,
  } = useAction(restProps);

  React.useEffect(() => {
    const onKeyPress = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose && onClose({}, 'escapeKeyDown');
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', onKeyPress);

    return () => {
      window.removeEventListener('keydown', onKeyPress);
    };
  }, [onClose]);

  const isSaveLoading = React.useMemo<boolean | undefined>(
    () => getLoading('saveContent', String(item?.id)),
    [getLoading, item?.id],
  );

  const handleUsageRightsDialog = () => {
    showModal(UsageRightsDialog, {});
  };

  const handleBuyContentDialog = () => {
    showModal(BuyContentDialog, {
      balance: availableBalance,
      onConfirm: handleBuyContent,
      cost: item?.price || 0,
    });
  };

  return (
    <Stack direction='row' spacing={2} alignItems='center'>
      <IconButton color='primary' onClick={() => handleNavigation('prev')}>
        <ArrowBackIcon />
      </IconButton>
      <Paper sx={{ position: 'relative', width: '1293px', height: '834px' }}>
        <IconButton
          color='primary'
          sx={{ position: 'absolute', right: 10, top: 10 }}
          onClick={() => onClose && onClose({}, 'backdropClick')}
        >
          <CloseIcon />
        </IconButton>

        <Box
          sx={{
            display: 'flex',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              height: '100%',
              width: '100%',
            }}
          >
            <Box sx={{ width: '50%', position: 'relative' }}>
              <MediaContent
                src={item?.images?.medium ?? ''}
                videoSrc={
                  item?.images?.video
                    ? `${baseUrl}/${item.content}`
                    : item?.images?.video || undefined
                }
              />
            </Box>
            <Box
              sx={{
                width: '50%',
                px: 5,
                py: 3,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <UserHeader
                item={item}
                handleInviteUser={handleInviteUser}
                handleSaveUser={handleSaveUser}
                getLoading={getLoading}
              />

              <Box>
                <Box
                  display={'flex'}
                  alignItems={'center'}
                  justifyContent={'space-between'}
                  mb={1}
                  gap={3}
                >
                  <RequirementContainer>
                    <Typography color='#5F5F60' fontSize={'15px'}>
                      Offer
                    </Typography>
                    <Box
                      component={Link}
                      to={
                        venueTags?.includes(VENUE_MANUAL_TAGS.Online)
                          ? `/products/${item?.offerID}`
                          : `/offers/${item?.offerID}`
                      }
                      color={'#000'}
                    >
                      {item?.offerTitle}
                    </Box>
                  </RequirementContainer>
                  {item?.postOnInstagramStories ||
                  item?.postOnInstagramStories3x ||
                  item?.postOnInstagramFeed ||
                  item?.postOnInstagramReels ? (
                    <RequirementContainer>
                      <Typography color='#5F5F60' fontSize={'15px'}>
                        Posted on
                      </Typography>
                      {item?.postOnInstagramStories || item.postOnInstagramStories3x ? (
                        <Box display={'flex'} alignItems={'center'} gap={1}>
                          <StoryIcon />
                          Story
                        </Box>
                      ) : item?.postOnInstagramFeed ? (
                        <Box display={'flex'} alignItems={'center'} gap={1}>
                          <FeedIcon />
                          Feed
                        </Box>
                      ) : item?.postOnInstagramReels ? (
                        <Box display={'flex'} alignItems={'center'} gap={1}>
                          <ReelIcon />
                          Reel
                        </Box>
                      ) : null}
                    </RequirementContainer>
                  ) : null}
                </Box>
                {item?.ispost ? (
                  <>
                    {item?.postLink && (
                      <Button
                        fullWidth
                        variant='contained'
                        color='primary'
                        component={'a'}
                        href={item?.postLink}
                        target='_blank'
                        sx={{ py: '14px', mb: 1 }}
                      >
                        Go to post
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    {isSaveLoading === false || item?.byVenue ? (
                      <>
                        {!item?.price ? (
                          <ActionBuyDownloadBeforeUpdate
                            purchasedDate={item?.downloadedAt || ''}
                            onDownload={handleDownloadContent}
                          />
                        ) : (
                          <ActionBuyDownload
                            purchasedDate={item?.downloadedAt || ''}
                            successMessage={isSaveLoading === false}
                            onUsageRights={handleUsageRightsDialog}
                            onDownload={handleDownloadContent}
                          />
                        )}
                      </>
                    ) : !item?.byVenue && item?.price ? (
                      <BuyContent
                        cost={item?.price || 0}
                        loading={isSaveLoading}
                        balance={availableBalance}
                        accountStatus={status}
                        onBuyContent={handleBuyContentDialog}
                        onUsageRight={handleUsageRightsDialog}
                      />
                    ) : !item?.byVenue && !item?.price ? (
                      <NoPriceBuyContent />
                    ) : null}
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>
      <IconButton color='primary' onClick={() => handleNavigation('next')}>
        <ArrowForwardIcon />
      </IconButton>
    </Stack>
  );
};

export default Content;

export const RequirementContainer = styled(Box)({
  borderRadius: '8px',
  border: '1px solid #EBE8E8',
  backgroundColor: '#F7F7F7',
  flex: 1,
  padding: '13px 15px',

  display: 'flex',
  alignItems: 'flex-start',
  flexDirection: 'column',
  gap: '6px',
});
